import { normalDirectionsOf } from '@hue/contracts';
import type { AccountingAccount, QueueRow } from './api.js';

const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

/// "2026-10" → "outubro de 2026".
export function monthName(month: string): string {
  const [year, number] = month.split('-');
  return `${MONTHS[Number(number) - 1] ?? month} de ${year}`;
}

export const DIFFERENCE_REASONS: Readonly<Record<string, string>> = {
  ROUNDING: 'Arredondamento',
  BANK_FEE: 'Tarifa bancária',
  CONFIRMED_DIFFERENCE: 'Diferença confirmada',
};

/// O que a conta escolhida faz com a linha, em palavras de quem não é contador.
export function effectOf(row: QueueRow): string {
  const outside = row.account?.code.startsWith('9.') ?? false;
  const outsideText =
    'Vale no Fluxo de caixa e fica fora do resultado, porque a conta é do grupo Fora do DRE.';
  switch (row.part) {
    case 'WAITING':
      return 'A conta vale só no Fluxo de caixa. No DRE, só depois de conciliar ou declarar sem compromisso.';
    case 'DECLARED':
    case 'DIFFERENCE':
      return outside
        ? outsideText
        : `Vale no Fluxo de caixa e no DRE de ${monthName(row.competenceMonth ?? '')}.`;
    case 'OBLIGATION':
      return outside
        ? 'Fica fora do resultado, porque a conta é do grupo Fora do DRE. O pagamento conciliado leva esta conta ao Fluxo de caixa.'
        : `Vale no DRE de ${monthName(row.competenceMonth ?? '')}, pago ou não. O pagamento conciliado leva esta conta ao Fluxo de caixa.`;
    default:
      return 'A conta passa para cada mês gerado a partir de agora. Os meses já gerados mudam um a um, em Compromissos.';
  }
}

export const PART_LABELS: Readonly<Record<QueueRow['part'], string>> = {
  WAITING: 'Aguardando conciliação',
  DECLARED: 'Sem compromisso',
  DIFFERENCE: 'Diferença resolvida',
  OBLIGATION: 'Compromisso',
  RECURRENCE: 'Recorrência',
};

export const ORIGIN_LABELS: Readonly<Record<NonNullable<QueueRow['origin']>, string>> = {
  MANUAL: 'Escolha manual',
  RULE: 'Por regra',
  FORM: 'No formulário',
  RECURRENCE: 'Da recorrência',
};

/// Entrada numa conta de despesa reduz a despesa; só vira estorno quando apontar o lançamento
/// original (ADR 0061, decisão 9).
export function isContrary(code: string, direction: 'INFLOW' | 'OUTFLOW'): boolean {
  const normal = normalDirectionsOf(code);
  return normal.length > 0 && !normal.includes(direction);
}

/// Contas em que se pode lançar: ativas e que não agrupam outras.
export function postableAccounts(accounts: readonly AccountingAccount[]): AccountingAccount[] {
  return accounts.filter(
    (account) =>
      account.active && !accounts.some((other) => other.code.startsWith(`${account.code}.`)),
  );
}

/// A diferença por tarifa já sugere a conta de tarifas do plano sugerido, se ela existir.
export function reasonSuggestion(
  row: QueueRow,
  accounts: readonly AccountingAccount[],
): AccountingAccount | null {
  if (row.part !== 'DIFFERENCE' || row.reason !== 'BANK_FEE' || row.account) return null;
  return postableAccounts(accounts).find((account) => account.code === '5.1.1') ?? null;
}

/// Partes do extrato que regra pode classificar (ADR 0061, decisão 19). Compromisso e recorrência
/// têm conta própria e nunca passam por regra.
const RULE_PARTS: ReadonlySet<QueueRow['part']> = new Set(['WAITING', 'DECLARED', 'DIFFERENCE']);

/// Quem classificou e quando, em uma linha: "Por regra «Enel»", "Escolha manual".
export function originOf(row: QueueRow): string {
  const origin =
    row.origin === 'RULE'
      ? row.ruleName
        ? `Por regra «${row.ruleName}»`
        : 'Por regra'
      : row.origin
        ? ORIGIN_LABELS[row.origin]
        : 'Com conta';
  return `${origin}${row.ruleRemoved ? ' · Regra removida' : ''}`;
}

/// O que as regras fizeram ou deixaram de fazer nesta linha. A pessoa precisa saber o que foi
/// automático e, quando não foi, por quê — sem isso, "Sem conta" parece descuido do sistema.
export function ruleNoteOf(row: QueueRow): string | null {
  if (row.account && row.origin === 'RULE') {
    switch (row.ruleTrigger) {
      case 'IMPORT':
        return 'Automática, na importação do extrato.';
      case 'UNRECONCILE':
        return 'Automática, quando a conciliação foi desfeita.';
      default:
        return 'Aplicada pela tela Regras de classificação.';
    }
  }
  if (!RULE_PARTS.has(row.part) || row.parcels) return null;
  const hint = row.ruleHint;
  const target = hint ? `${hint.account.code} ${hint.account.name}` : '';
  if (row.account) {
    if (!hint || hint.suspended || hint.account.code === row.account.code) return null;
    return `A regra «${hint.name}» indicaria ${target}, mas vale a ${
      row.origin === 'FORM' ? 'conta escolhida no formulário' : 'escolha manual'
    }: regra nunca muda o que uma pessoa escolheu.`;
  }
  if (!hint) return 'Nenhuma regra casa com este lançamento.';
  if (hint.suspended) {
    return `A regra «${hint.name}» casa, mas está suspensa: a conta ${target} foi desativada ou passou a agrupar outras.`;
  }
  return `A regra «${hint.name}» (${target}) casa, mas só age sozinha no extrato que chega depois dela. Para este, use Aplicar aos sem conta, em Regras de classificação.`;
}
